// ============================================================================
// ContactTeacherPage - Shown when student has invalid program configuration
// ============================================================================

import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  Divider,
  CircularProgress,
} from "@mui/material";
import {
  ErrorOutline as ErrorIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  School as SchoolIcon,
  BugReport as BugIcon,
} from "@mui/icons-material";
import { useAuth } from "../../hooks/useAuth";
import { getUserProfile } from "../../services/userService";
import { StudentProfile } from "../../utils/studentUtils";
import { EapProgram } from "../../types/School";
import { useSnackbar } from "notistack";

const ContactTeacherPage: React.FC = () => {
  const { user, setUser, signOut } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const profile = user as StudentProfile | null;
  const teacher = profile?.myClassroom?.teacher;
  const studentName = profile
    ? `${profile.firstName} ${profile.lastName}`
    : "Student";
  const invalidProgramName = profile?.myClassroom?.programName || "Unknown";

  // Valid program names for display
  const validProgramNames = Object.values(EapProgram);

  // Build mailto link for error report
  const buildMailtoLink = () => {
    const recipient = teacher?.email || "support@eapspace.com";
    const subject = encodeURIComponent(
      "Program Configuration Issue - EAP Space"
    );
    const body = encodeURIComponent(
      `Dear ${teacher?.name || "Support Team"},\n\n` +
        `I am encountering a technical issue with my EAP Space account.\n\n` +
        `Issue Details:\n` +
        `- My assigned program "${invalidProgramName}" is not recognized by the system.\n` +
        `- Valid programs are: ${validProgramNames.join(", ")}\n\n` +
        `Student Information:\n` +
        `- Name: ${studentName}\n` +
        `- Email: ${profile?.email || "N/A"}\n` +
        `- Classroom ID: ${profile?.myClassroom?.classroomId || "N/A"}\n\n` +
        `Could you please update my program assignment?\n\n` +
        `Thank you,\n${studentName}`
    );

    return `mailto:${recipient}?subject=${subject}&body=${body}`;
  };

  // Refresh profile to check if issue is resolved
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await getUserProfile();
      const updatedProfile = response?.data;

      if (updatedProfile) {
        setUser(updatedProfile);

        const programName = updatedProfile.myClassroom?.programName;
        if (
          programName &&
          validProgramNames.includes(programName as EapProgram)
        ) {
          enqueueSnackbar("Issue resolved! Redirecting to your classroom...", {
            variant: "success",
          });
          // The StudentGuard will handle the redirect
          window.location.reload();
        } else {
          enqueueSnackbar("Issue still present. Please contact your teacher.", {
            variant: "warning",
          });
        }
      }
    } catch (error) {
      enqueueSnackbar("Failed to refresh. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          textAlign: "center",
          borderRadius: 2,
        }}
      >
        {/* Icon */}
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            backgroundColor: "error.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <ErrorIcon sx={{ fontSize: 40, color: "error.main" }} />
        </Box>

        {/* Title */}
        <Typography variant="h4" gutterBottom fontWeight={600}>
          Configuration Issue
        </Typography>

        {/* Message */}
        <Typography variant="body1" color="text.secondary" paragraph>
          There's a problem with your classroom configuration that needs to be
          fixed by your teacher. Don't worry - this is a technical issue, not
          something you did wrong!
        </Typography>

        {/* Technical Details (for teacher reference) */}
        <Alert
          severity="warning"
          icon={<BugIcon />}
          sx={{ my: 3, textAlign: "left" }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Technical Details (share with your teacher):
          </Typography>
          <Typography variant="body2" component="div">
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              <li>
                <strong>Assigned Program:</strong> "{invalidProgramName}"
              </li>
              <li>
                <strong>Valid Programs:</strong> {validProgramNames.join(", ")}
              </li>
              <li>
                <strong>Classroom ID:</strong>{" "}
                {profile?.myClassroom?.classroomId || "N/A"}
              </li>
            </Box>
          </Typography>
        </Alert>

        <Divider sx={{ my: 3 }} />

        {/* Teacher Contact Card */}
        {teacher ? (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <SchoolIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="subtitle1" fontWeight={500}>
                  Your Teacher
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                {teacher.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {teacher.email}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="body2" color="text.secondary">
                No teacher contact information available.
                <br />
                Please contact support at{" "}
                <a href="mailto:support@eapspace.com">support@eapspace.com</a>
              </Typography>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EmailIcon />}
            href={buildMailtoLink()}
            fullWidth
          >
            Report Issue to Teacher
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={
              isRefreshing ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <RefreshIcon />
              )
            }
            onClick={handleRefresh}
            disabled={isRefreshing}
            fullWidth
          >
            {isRefreshing ? "Checking..." : "Check if Fixed"}
          </Button>

          <Button
            variant="outlined"
            color="inherit"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            fullWidth
          >
            Sign Out
          </Button>
        </Box>

        {/* Footer Note */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3 }}
        >
          This issue typically gets resolved within a few hours once reported.
        </Typography>
      </Paper>
    </Container>
  );
};

export default ContactTeacherPage;
